var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '../../styles/widgets/popup/arrayModelManipulativePopup.scss';
import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../../player/DataContext';
import FooterActionsButtons from './FooterActionsButtons';
import ArrayModel from '../ArrayModel';
import RadioButton from '../../components/RadioButton';
import ButtonComponent from '../../components/ButtonComponent';
import InputBox from '../../components/InputBox';
import globalStore from '../../store';
import CustomScrollbar from '../../components/CustomScrollbar';
import { saveState } from '../../helper/stateManagerHelper';
import { useOnResize } from '../../helper/hooks/onResize';
var generateId = function () { return "arrayModel_".concat(uuidv4().replace(/-/g, '')); };
var DEFAULT_DATA = {
    id: generateId(),
    type: 'arrayModels',
    mode: 'grid',
    preview: false,
    rows: 5,
    columns: 5,
    cellValue: 1,
    scale: 1,
    center: { x: 0, y: 0 },
    dimensions: { width: 550, height: 550 },
    lock: { positioning: false, editing: false },
};
var ArrayModelManipulativePopup = function (_a) {
    var _b = _a.data, data = _b === void 0 ? DEFAULT_DATA : _b;
    var _c = useContext(DataContext), setManipulative = _c.setManipulative, setSelectedManipulativeId = _c.setSelectedManipulativeId, setManiplulatorComponentVisible = _c.setManiplulatorComponentVisible, setActiveManipulator = _c.setActiveManipulator, getPositionForNewItem = _c.getPositionForNewItem, setSelectedTool = _c.setSelectedTool;
    var t = useTranslation().t;
    var _d = useState(data), arrayData = _d[0], setArrayData = _d[1];
    var _e = useState(String(data.rows)), rowInput = _e[0], setRowInput = _e[1];
    var _f = useState(String(data.columns)), colInput = _f[0], setColInput = _f[1];
    var _g = useState(''), popupAnnounceText = _g[0], setPopupAnnounceText = _g[1];
    var isRowError = arrayData.mode !== 'open' &&
        (rowInput === '' || isNaN(Number(rowInput)) || Number(rowInput) < 1 || Number(rowInput) > 50);
    var isColError = arrayData.mode !== 'open' &&
        (colInput === '' || isNaN(Number(colInput)) || Number(colInput) < 1 || Number(colInput) > 50);
    var hasError = isRowError || isColError;
    var btnConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).btnConfig;
    var previewContainerRef = useRef(null);
    var initializedRef = useRef(false);
    useEffect(function () {
        setArrayData(function (prev) { return (__assign(__assign({}, prev), { preview: true })); });
    }, []);
    // -------------------------------
    // Live announcements for screen readers (WCAG 4.1.3)
    // Uses an internal aria-live region inside this aria-modal dialog so
    // announcements are not blocked by the modal boundary.
    // -------------------------------
    var popupAnnounce = function (msg) {
        setPopupAnnounceText('');
        requestAnimationFrame(function () { return setPopupAnnounceText(msg); });
    };
    var prevModeRef = useRef(arrayData.mode);
    useEffect(function () {
        if (prevModeRef.current !== arrayData.mode) {
            var modeLabel = arrayData.mode === 'grid'
                ? t('text.arrayModelGridArray')
                : t('text.arrayModelOpenArray');
            popupAnnounce("".concat(modeLabel, " ").concat(t('text.selected')));
            prevModeRef.current = arrayData.mode;
        }
    }, [arrayData.mode, t]);
    // -------------------------------
    // Scaling logic (similar to NumberChart)
    // -------------------------------
    var UpdatePos = useCallback(function () {
        var container = previewContainerRef.current;
        var popup = container === null || container === void 0 ? void 0 : container.querySelector('.arrayModelWrapper');
        if (!container || !popup)
            return;
        popup.style.transform = 'scale(1)';
        popup.style.transformOrigin = 'center';
        popup.classList.remove('scaled');
        var containerWidth = container.offsetWidth;
        var containerHeight = container.offsetHeight;
        var popupWidth = popup.offsetWidth;
        var popupHeight = popup.offsetHeight;
        if (!popupWidth || !popupHeight)
            return;
        var widthScale = containerWidth / popupWidth;
        var heightScale = containerHeight / popupHeight;
        var scale = widthScale < heightScale ? widthScale : heightScale;
        popup.style.transform = "scale(".concat(scale, ")");
        popup.classList.add('scaled');
    }, []);
    var handleResize = useCallback(function () {
        UpdatePos();
    }, [UpdatePos]);
    useOnResize(handleResize);
    // Initial call: wait for layout to fully settle before first scale
    useEffect(function () {
        if (!initializedRef.current) {
            initializedRef.current = true;
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    UpdatePos();
                });
            });
            return;
        }
        // UpdatePos();
    }, [UpdatePos, arrayData.rows, arrayData.columns, arrayData.mode]);
    // -------------------------------
    // Helpers
    // -------------------------------
    var updateRows = function (delta) {
        setArrayData(function (prev) {
            var next = Math.min(50, Math.max(1, prev.rows + delta));
            if (next === prev.rows) {
                popupAnnounce(next <= 1
                    ? t('text.arrayModelMinReached', { label: t('text.rows') })
                    : t('text.arrayModelMaxReached', { label: t('text.rows') }));
            }
            else {
                var key = delta > 0 ? 'text.arrayModelRowsIncreased' : 'text.arrayModelRowsDecreased';
                popupAnnounce(t(key, { count: next }));
            }
            setRowInput(String(next));
            return __assign(__assign({}, prev), { rows: next });
        });
    };
    var updateCols = function (delta) {
        setArrayData(function (prev) {
            var next = Math.min(50, Math.max(1, prev.columns + delta));
            if (next === prev.columns) {
                popupAnnounce(next <= 1
                    ? t('text.arrayModelMinReached', { label: t('text.columns') })
                    : t('text.arrayModelMaxReached', { label: t('text.columns') }));
            }
            else {
                var key = delta > 0 ? 'text.arrayModelColumnsIncreased' : 'text.arrayModelColumnsDecreased';
                popupAnnounce(t(key, { count: next }));
            }
            setColInput(String(next));
            return __assign(__assign({}, prev), { columns: next });
        });
    };
    var handleRowInput = function (_id, val) {
        var digits = val.replace(/[^0-9]/g, '');
        setRowInput(digits);
        var num = parseInt(digits, 10);
        if (!isNaN(num) && num >= 1 && num <= 50) {
            setArrayData(function (prev) { return (__assign(__assign({}, prev), { rows: num })); });
        }
    };
    var handleColInput = function (_id, val) {
        var digits = val.replace(/[^0-9]/g, '');
        setColInput(digits);
        var num = parseInt(digits, 10);
        if (!isNaN(num) && num >= 1 && num <= 50) {
            setArrayData(function (prev) { return (__assign(__assign({}, prev), { columns: num })); });
        }
    };
    var handleRowChange = function (_id, eventType) {
        if (eventType === 'blur') {
            var num = parseInt(rowInput, 10);
            if (isNaN(num) || num < 1 || num > 50) {
                setRowInput(String(arrayData.rows));
            }
        }
    };
    var handleColChange = function (_id, eventType) {
        if (eventType === 'blur') {
            var num = parseInt(colInput, 10);
            if (isNaN(num) || num < 1 || num > 50) {
                setColInput(String(arrayData.columns));
            }
        }
    };
    var handleNumericKeyDown = function (e) {
        var allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'];
        if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };
    // -------------------------------
    // Actions
    // -------------------------------
    var onAction = function (type) {
        if (type === 'done') {
            var cellSize = 55;
            // Open arrays default to 10x10, grid arrays use the configured size
            var rows = arrayData.mode === 'grid' ? arrayData.rows : 10;
            var columns = arrayData.mode === 'grid' ? arrayData.columns : 10;
            var _a = getPositionForNewItem(), x = _a.x, y = _a.y;
            var newArrayId = generateId();
            var newManipulative_1 = {
                type: 'arrayModel',
                widgetId: "arrayModel:".concat(uuidv4()),
                data: __assign(__assign({}, arrayData), { id: newArrayId, rows: rows, columns: columns, dimensions: {
                        width: columns * cellSize,
                        height: rows * cellSize,
                    }, center: { x: x, y: y }, preview: false, cutRows: [], cutColumns: [], openCuts: { rows: [], cols: [] }, showExpression: false, showProduct: false, partColors: {}, partModeEnabled: true, selectModeEnabled: false }),
            };
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [
                newManipulative_1,
            ], false); });
            // Save state for undo/redo
            saveState({
                type: newArrayId,
                id: newArrayId.split('_')[1],
                propertyName: 'create',
                old: JSON.stringify(newManipulative_1),
            });
            // Auto-select the newly created array model
            setSelectedTool([newArrayId]);
        }
        setSelectedManipulativeId('');
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    // -------------------------------
    // UI
    // -------------------------------
    return (_jsxs("div", { className: "arrayPopup", role: "dialog", "aria-labelledby": "arrayPopupTitle", "aria-modal": "true", children: [_jsx("div", { "aria-live": "assertive", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: popupAnnounceText }), _jsxs("div", { className: "holder", children: [_jsx(CustomScrollbar, { direction: "vertical", children: _jsxs("div", { className: "scrollbarContent", children: [_jsxs("div", { className: "arrayHeader", children: [_jsx("h2", { className: "title", id: "arrayPopupTitle", children: t('text.arrayModelPopupTitle') }), _jsxs("fieldset", { className: "arrayTypeSelector", children: [_jsx("legend", { style: {
                                                        position: 'absolute',
                                                        width: '1px',
                                                        height: '1px',
                                                        padding: 0,
                                                        margin: '-1px',
                                                        overflow: 'hidden',
                                                        clip: 'rect(0, 0, 0, 0)',
                                                        whiteSpace: 'nowrap',
                                                        border: 0,
                                                    }, children: t('text.arrayModelArrayType') }), _jsx(RadioButton, { id: "array-mode-selector", options: [t('text.arrayModelGridArray'), t('text.arrayModelOpenArray')], selected: arrayData.mode === 'grid' ? 0 : 1, onChange: function (index) {
                                                        var newMode = index === 0 ? 'grid' : 'open';
                                                        setArrayData(function (prev) { return (__assign(__assign({}, prev), { mode: newMode })); });
                                                    }, className: "arrayRadioInline", groupAriaLabel: t('text.arrayModelArrayType') })] })] }), _jsx("div", { className: "arrayPreview", ref: previewContainerRef, role: "img", "aria-label": arrayData.mode === 'grid'
                                        ? t('text.arrayModelAriaGridDesc', { rows: arrayData.rows, columns: arrayData.columns })
                                        : t('text.arrayModelOpenArray'), children: _jsx(ArrayModel, { data: __assign(__assign({}, arrayData), { center: { x: 0, y: 0 } }) }) }), _jsx("div", { className: "operationContainer", children: _jsxs("div", { className: "headerWrapper", children: [_jsxs("div", { className: "leftContainer", role: "group", "aria-labelledby": "rows-label", children: [_jsxs("span", { id: "rows-label", className: arrayData.mode === 'open' ? 'displayCount_disable' : '', children: [_jsx("b", { children: t('text.rows') }), "\u00A0", _jsx("span", { id: "rows-range-hint", children: t('text.arrayModel1to50Range') })] }), _jsx("div", { className: "dividerLine" }), _jsx(ButtonComponent, { imgSrc: btnConfig.minus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateRows(-1); }, disabled: arrayData.rows <= 1 || arrayData.mode === 'open', tabIndex: arrayData.mode === 'open' ? -1 : undefined, clickActive: false, ariaLabel: t('text.arrayModelDecreaseRows') }), _jsx(InputBox, { id: "row-input", value: rowInput, type: "text", maxLength: 2, inputMode: "numeric", pattern: "[0-9]*", defaultCSS: "displayCount".concat(isRowError ? ' displayCount_error' : ''), normalCSS: "displayCount", onInput: handleRowInput, onChange: handleRowChange, onKeyDown: handleNumericKeyDown, disabled: arrayData.mode === 'open', ariaLabel: t('text.rows'), ariaDescribedBy: "rows-range-hint".concat(isRowError ? ' rows-error' : ''), ariaInvalid: isRowError }), _jsx(ButtonComponent, { imgSrc: btnConfig.plus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateRows(1); }, disabled: arrayData.rows >= 50 || arrayData.mode === 'open', tabIndex: arrayData.mode === 'open' ? -1 : undefined, clickActive: false, ariaLabel: t('text.arrayModelIncreaseRows') }), _jsx("div", { className: "dividerLine" }), isRowError && (_jsx("span", { id: "rows-error", role: "alert", style: {
                                                            position: 'absolute',
                                                            width: '1px',
                                                            height: '1px',
                                                            padding: 0,
                                                            margin: '-1px',
                                                            overflow: 'hidden',
                                                            clip: 'rect(0, 0, 0, 0)',
                                                            whiteSpace: 'nowrap',
                                                            border: 0,
                                                        }, children: t('text.arrayModelRowError') }))] }), _jsxs("div", { className: "rightContainer", role: "group", "aria-labelledby": "cols-label", children: [_jsxs("span", { id: "cols-label", className: arrayData.mode === 'open' ? 'displayCount_disable' : '', children: [_jsx("b", { children: t('text.columns') }), "\u00A0", _jsx("span", { id: "cols-range-hint", children: t('text.arrayModel1to50Range') })] }), _jsx("div", { className: "dividerLine" }), _jsx(ButtonComponent, { imgSrc: btnConfig.minus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateCols(-1); }, disabled: arrayData.columns <= 1 || arrayData.mode === 'open', tabIndex: arrayData.mode === 'open' ? -1 : undefined, clickActive: false, ariaLabel: t('text.arrayModelDecreaseColumns') }), _jsx(InputBox, { id: "col-input", value: colInput, type: "text", maxLength: 2, inputMode: "numeric", pattern: "[0-9]*", defaultCSS: "displayCount".concat(isColError ? ' displayCount_error' : ''), normalCSS: "displayCount", onInput: handleColInput, onChange: handleColChange, onKeyDown: handleNumericKeyDown, disabled: arrayData.mode === 'open', ariaLabel: t('text.columns'), ariaDescribedBy: "cols-range-hint".concat(isColError ? ' cols-error' : ''), ariaInvalid: isColError }), _jsx(ButtonComponent, { imgSrc: btnConfig.plus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateCols(1); }, disabled: arrayData.columns >= 50 || arrayData.mode === 'open', tabIndex: arrayData.mode === 'open' ? -1 : undefined, clickActive: false, ariaLabel: t('text.arrayModelIncreaseColumns') }), _jsx("div", { className: "dividerLine" }), isColError && (_jsx("span", { id: "cols-error", role: "alert", style: {
                                                            position: 'absolute',
                                                            width: '1px',
                                                            height: '1px',
                                                            padding: 0,
                                                            margin: '-1px',
                                                            overflow: 'hidden',
                                                            clip: 'rect(0, 0, 0, 0)',
                                                            whiteSpace: 'nowrap',
                                                            border: 0,
                                                        }, children: t('text.arrayModelColError') }))] })] }) })] }) }), _jsx(FooterActionsButtons, { onActionBtnClick: onAction, disableDoneBtn: hasError })] })] }));
};
export default ArrayModelManipulativePopup;
